/**
 * The basics: the handful of facts that are not opinions.
 *
 * v1 mixed these in with everything else, which made it impossible to tell at
 * a glance what was being *used* and what was merely being *stored*. Here the
 * split is structural:
 *
 *   - This file holds the fixed fields. They identify you, and three of them
 *     act as hard filters that no similarity score can override.
 *   - Everything else lives in Neo4j as :Question nodes, is editable from
 *     /admin, and is answered on the single -3..+3 scale.
 *
 * Nothing in here is scored. Gender, age and language either let a pair
 * through or they don't.
 */

// ---------------------------------------------------------------------------
// Gender
// ---------------------------------------------------------------------------

export interface Choice {
  id: string;
  label: string;
  emoji?: string;
}

export const GENDERS: Choice[] = [
  { id: "woman", label: "Woman", emoji: "♀" },
  { id: "man", label: "Man", emoji: "♂" },
  { id: "nonbinary", label: "Non-binary", emoji: "⚧" },
  { id: "genderfluid", label: "Genderfluid", emoji: "🌀" },
  { id: "agender", label: "Agender", emoji: "○" },
  { id: "other", label: "Other", emoji: "✳" },
];

export const GENDER_IDS = GENDERS.map((g) => g.id);

// ---------------------------------------------------------------------------
// Languages
// ---------------------------------------------------------------------------

/**
 * Spoken languages. This is a hard filter in both directions: two people must
 * share at least one, or they can't talk to each other and nothing else about
 * the match matters.
 */
const LANGUAGE_DATA = `en:English|de:German|fr:French|es:Spanish|it:Italian|pt:Portuguese
nl:Dutch|sv:Swedish|no:Norwegian|da:Danish|fi:Finnish|is:Icelandic|pl:Polish
cs:Czech|sk:Slovak|hu:Hungarian|ro:Romanian|bg:Bulgarian|hr:Croatian|sr:Serbian
sl:Slovenian|sq:Albanian|el:Greek|tr:Turkish|ru:Russian|uk:Ukrainian|be:Belarusian
lt:Lithuanian|lv:Latvian|et:Estonian|he:Hebrew|ar:Arabic|fa:Persian|ur:Urdu
hi:Hindi|bn:Bengali|pa:Punjabi|ta:Tamil|te:Telugu|mr:Marathi|gu:Gujarati
zh:Mandarin|yue:Cantonese|ja:Japanese|ko:Korean|vi:Vietnamese|th:Thai|km:Khmer
id:Indonesian|ms:Malay|tl:Filipino|sw:Swahili|am:Amharic|ha:Hausa|yo:Yoruba
af:Afrikaans|ga:Irish|cy:Welsh|ca:Catalan|eu:Basque|gl:Galician|mt:Maltese
ka:Georgian|hy:Armenian|az:Azerbaijani|kk:Kazakh|uz:Uzbek|mn:Mongolian|ne:Nepali
si:Sinhala|my:Burmese|lo:Lao|sign:Sign language|other:Something else`;

export const LANGUAGES: Choice[] = LANGUAGE_DATA.split(/[\n|]/)
  .map((s) => s.trim())
  .filter(Boolean)
  .map((s) => {
    const [id, label] = s.split(":");
    return { id: id!, label: label! };
  });

export const LANGUAGE_IDS = new Set(LANGUAGES.map((l) => l.id));

export function languageLabel(id: string): string {
  return LANGUAGES.find((l) => l.id === id)?.label ?? id;
}

// ---------------------------------------------------------------------------
// Countries
// ---------------------------------------------------------------------------

/**
 * ISO 3166-1 alpha-2. Only the code and name are stored; the flag emoji is
 * derived from the code by mapping each letter to its regional indicator
 * symbol, so there is no third column to keep in sync.
 */
const COUNTRY_DATA = `AF:Afghanistan|AL:Albania|DZ:Algeria|AD:Andorra|AO:Angola|AG:Antigua and Barbuda
AR:Argentina|AM:Armenia|AU:Australia|AT:Austria|AZ:Azerbaijan|BS:Bahamas|BH:Bahrain
BD:Bangladesh|BB:Barbados|BY:Belarus|BE:Belgium|BZ:Belize|BJ:Benin|BT:Bhutan|BO:Bolivia
BA:Bosnia and Herzegovina|BW:Botswana|BR:Brazil|BN:Brunei|BG:Bulgaria|BF:Burkina Faso
BI:Burundi|KH:Cambodia|CM:Cameroon|CA:Canada|CV:Cape Verde|CF:Central African Republic
TD:Chad|CL:Chile|CN:China|CO:Colombia|KM:Comoros|CG:Congo|CD:Congo (DRC)|CR:Costa Rica
CI:Côte d'Ivoire|HR:Croatia|CU:Cuba|CY:Cyprus|CZ:Czechia|DK:Denmark|DJ:Djibouti
DM:Dominica|DO:Dominican Republic|EC:Ecuador|EG:Egypt|SV:El Salvador|GQ:Equatorial Guinea
ER:Eritrea|EE:Estonia|SZ:Eswatini|ET:Ethiopia|FJ:Fiji|FI:Finland|FR:France|GA:Gabon
GM:Gambia|GE:Georgia|DE:Germany|GH:Ghana|GR:Greece|GD:Grenada|GT:Guatemala|GN:Guinea
GW:Guinea-Bissau|GY:Guyana|HT:Haiti|HN:Honduras|HK:Hong Kong|HU:Hungary|IS:Iceland
IN:India|ID:Indonesia|IR:Iran|IQ:Iraq|IE:Ireland|IL:Israel|IT:Italy|JM:Jamaica|JP:Japan
JO:Jordan|KZ:Kazakhstan|KE:Kenya|KI:Kiribati|KW:Kuwait|KG:Kyrgyzstan|LA:Laos|LV:Latvia
LB:Lebanon|LS:Lesotho|LR:Liberia|LY:Libya|LI:Liechtenstein|LT:Lithuania|LU:Luxembourg
MO:Macao|MG:Madagascar|MW:Malawi|MY:Malaysia|MV:Maldives|ML:Mali|MT:Malta
MH:Marshall Islands|MR:Mauritania|MU:Mauritius|MX:Mexico|FM:Micronesia|MD:Moldova
MC:Monaco|MN:Mongolia|ME:Montenegro|MA:Morocco|MZ:Mozambique|MM:Myanmar|NA:Namibia
NR:Nauru|NP:Nepal|NL:Netherlands|NZ:New Zealand|NI:Nicaragua|NE:Niger|NG:Nigeria
KP:North Korea|MK:North Macedonia|NO:Norway|OM:Oman|PK:Pakistan|PW:Palau|PS:Palestine
PA:Panama|PG:Papua New Guinea|PY:Paraguay|PE:Peru|PH:Philippines|PL:Poland|PT:Portugal
QA:Qatar|RO:Romania|RU:Russia|RW:Rwanda|KN:Saint Kitts and Nevis|LC:Saint Lucia
VC:Saint Vincent and the Grenadines|WS:Samoa|SM:San Marino|ST:São Tomé and Príncipe
SA:Saudi Arabia|SN:Senegal|RS:Serbia|SC:Seychelles|SL:Sierra Leone|SG:Singapore
SK:Slovakia|SI:Slovenia|SB:Solomon Islands|SO:Somalia|ZA:South Africa|KR:South Korea
SS:South Sudan|ES:Spain|LK:Sri Lanka|SD:Sudan|SR:Suriname|SE:Sweden|CH:Switzerland
SY:Syria|TW:Taiwan|TJ:Tajikistan|TZ:Tanzania|TH:Thailand|TL:Timor-Leste|TG:Togo
TO:Tonga|TT:Trinidad and Tobago|TN:Tunisia|TR:Türkiye|TM:Turkmenistan|TV:Tuvalu
UG:Uganda|UA:Ukraine|AE:United Arab Emirates|GB:United Kingdom|US:United States
UY:Uruguay|UZ:Uzbekistan|VU:Vanuatu|VA:Vatican City|VE:Venezuela|VN:Vietnam
YE:Yemen|ZM:Zambia|ZW:Zimbabwe`;

export interface Country {
  code: string;
  name: string;
  flag: string;
}

/** 🇩🇪 from "DE" — regional indicator symbols are just A-Z offset. */
export function flagFor(code: string): string {
  if (!/^[A-Z]{2}$/.test(code)) return "🏳";
  return String.fromCodePoint(
    ...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  );
}

export const COUNTRIES: Country[] = COUNTRY_DATA.split(/[\n|]/)
  .map((s) => s.trim())
  .filter(Boolean)
  .map((s) => {
    const i = s.indexOf(":");
    const code = s.slice(0, i);
    return { code, name: s.slice(i + 1), flag: flagFor(code) };
  })
  .sort((a, b) => a.name.localeCompare(b.name));

export const COUNTRY_CODES = new Set(COUNTRIES.map((c) => c.code));

export function countryName(code: string | null | undefined): string | null {
  if (!code) return null;
  return COUNTRIES.find((c) => c.code === code)?.name ?? null;
}

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

export const AGE_FLOOR = 18;
export const AGE_CEILING = 99;

export const LIMITS = {
  name: 40,
  locationText: 60,
  // Both bios are generous enough that nobody writing in good faith ever meets
  // the limit; it exists only to keep a single request from carrying an
  // unbounded blob.
  publicBio: 16000,
  bio: 16000,
  contact: 500,
  publicPassword: 64,
} as const;

/**
 * Short enough to say out loud, long enough not to fall to the eight guesses a
 * quarter of an hour the unlock endpoint allows. Both sides check it — the
 * server because it must, the form because a rejection after typing is worse
 * than a hint before.
 */
export const MIN_PUBLIC_PASSWORD = 6;

/** Whole years between a birthday and today, or null if unparseable. */
export function ageFromBirthday(birthday: unknown): number | null {
  if (typeof birthday !== "string" || !birthday) return null;
  const dob = new Date(birthday);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getUTCFullYear() - dob.getUTCFullYear();
  const months = now.getUTCMonth() - dob.getUTCMonth();
  if (months < 0 || (months === 0 && now.getUTCDate() < dob.getUTCDate())) age--;
  return age >= 0 && age < 130 ? age : null;
}

/** ISO date string for the latest birthday that still makes someone `age`. */
export function birthdayCutoff(age: number): string {
  const d = new Date();
  d.setUTCFullYear(d.getUTCFullYear() - age);
  return d.toISOString().slice(0, 10);
}
